// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react'
import { useIntl } from 'react-intl'
// TODO: Fix useBasket import - verify correct export name in msp-integrations
import {
  useBasket,
  useConfig,
  useCookie,
  getEventItemFromProduct
} from 'msp-integrations'
import {
  AlertBox,
  ButtonLabelProps,
  ButtonTypeProps,
  BrandCommerceBoxProps,
  CommerceBox,
  CommerceBoxAnchorLink,
  CommerceBoxProductProps,
  EventsEmitter,
  Events,
  Icons,
  Button,
  WishlistButton,
  PromoCode,
  dispatchChoiceOfBonusModal
  // TODO: fix the msp-components missing module declaration
} from 'msp-components'
import useProduct from '@/hooks/use-product'
import { validateProduct } from './JasminCommerceBox.helpers'
import * as S from './JasminCommerceBox.styles'

const JasminCommerceBox = ({
  data,
  onAddToCart,
  onQuantityChange,
  onVariantChange,
  onDelete,
  noPadding,
  smallDiscountPrice = false,
  showTaxesInfo = false,
  noVariants = false,
  isPDP = false,
  updateOnVariantChange = false,
  isDarkMode = false,
  isMobile = false,
  isWishlistPage = false,
  onWishlistChange,
  showWishListButton = true,
  showLowestPrice = false,
  handleAddToCart
}: BrandCommerceBoxProps) => {
  const {
    type,
    productId,
    product,
    showAnchorLink = false,
    addButtonAsIcon
  } = data
  const { eventTracking } = useCookie()
  const { formatMessage } = useIntl()
  const [productSelected, setProductSelected] = useState<
    CommerceBoxProductProps | string | undefined
  >(productId || product)
  const parsedProduct = useProduct(
    validateProduct(product || productSelected, noVariants)
  )

  const [isVirtualGiftCard, setIsVirtualGiftCard] = useState(true)
  const basket = useBasket()
  const { showLoading } = parsedProduct || { showLoading: true } // If product data is provided by parent
  const alertBoxRef = useRef<AlertBox>()
  const addToCartButtonRef = useRef()
  const scrollIntoViewElementRef = useRef()
  const [handleAddToCartError, setHandleAddToCartError] = useState<{
    [k: string]: any
  }>({})
  const [inventoryOverride, setInventoryOverride] = useState<any>(null)
  const buttonKey = (addButtonAsIcon || type) as keyof typeof S.BUTTON_TYPES
  const CTAButton = S.BUTTON_TYPES[buttonKey]

  const titleCantAddProduct = formatMessage({
    id: 'msp.commerce_box.add_to_cart.cannot_add_title',
    defaultMessage: 'Cannot add product to basket'
  })
  const descriptionCantAddProduct = formatMessage({
    id: 'msp.commerce_box.add_to_cart.cannot_add_physical_product',
    defaultMessage:
      'Physical products cannot be added to the basket if a digital product has already been selected.'
  })
  const titleNoOrderableProduct = formatMessage({
    id: 'msp.commerce_box.add_to_cart.not_orderable_item.title',
    defaultMessage: 'Item(s) could not be added to the basket'
  })
  const titleSoldOut = formatMessage({
    id: 'msp.commerce_box.add_to_cart.sold_out.title',
    defaultMessage: 'Unfortunately sold out!'
  })
  const descriptionNoOrderableProduct = formatMessage({
    id: 'msp.commerce_box.add_to_cart.not_orderable_item.message',
    defaultMessage:
      'At least one item from this set is not orderable at the moment and could not be added to the basket.'
  })
  const getProductItemNotAvailableDescription = (
    stockLevel: number | null,
    cartQuantity: number
  ) =>
    stockLevel !== null && stockLevel > 0 && stockLevel <= cartQuantity
      ? formatMessage(
          {
            id: 'msp.commerce_box.add_to_cart.not_available_quantity.max_in_cart_message',
            defaultMessage:
              'You already have the maximum available quantity ({stockLevel}) in your cart.'
          },
          { stockLevel }
        )
      : stockLevel !== null && stockLevel > 0
      ? formatMessage(
          {
            id: 'msp.commerce_box.add_to_cart.not_available_quantity.message',
            defaultMessage:
              'The desired quantity is unfortunately not available. Currently, only {stockLevel} units are in stock.'
          },
          { stockLevel }
        )
      : stockLevel === 0
      ? formatMessage({
          id: 'msp.commerce_box.add_to_cart.sold_out.message',
          defaultMessage:
            'Unfortunately sold out! The item is temporarily unavailable.'
        })
      : formatMessage({
          id: 'msp.commerce_box.add_to_cart.not_available_quantity.unknown_stock_message',
          defaultMessage:
            'The desired quantity is unfortunately not available. Please reduce the quantity and try again.'
        })

  const {
    theme: {
      mspComponents: { wishlistButton }
    }
  } = useConfig()

  const parseErrorPayload = (error: any) => {
    if (!error) return {}

    if (error.response?.data) return error.response.data

    if (typeof error.body === 'string') {
      try {
        return JSON.parse(error.body)
      } catch (_) {
        return error
      }
    }

    if (error.body && typeof error.body === 'object') {
      return error.body
    }

    if (typeof error.message === 'string') {
      try {
        return JSON.parse(error.message)
      } catch (_) {
        return error
      }
    }

    return error
  }

  const extractStockLevelFromMessage = (message: string) => {
    if (!message) return null

    const patterns = [
      /only\s+'?(\d+)'?\s*(?:units?|items?|pieces?)?\s*(?:are|is)?\s*in stock/i,
      /currently\s*,?\s*only\s+'?(\d+)'?/i,
      /available\s*(?:stock|quantity)?\s*[:=]\s*'?(\d+)'?/i
    ]

    for (const pattern of patterns) {
      const match = message.match(pattern)
      if (match?.[1]) {
        return Number(match[1])
      }
    }

    return null
  }

  const getAddToCartErrorState = async (error: any) => {
    const normalizedError = parseErrorPayload(error)
    const { statusCode = '', statusMessage = '' } =
      normalizedError?.arguments || {}

    const errorType = (normalizedError?.type || '').toLowerCase()
    const errorMessage =
      normalizedError?.detail ||
      normalizedError?.title ||
      normalizedError?.message ||
      statusMessage ||
      ''

    const isProductItemNotAvailable =
      errorType === 'productitemnotavailableexception' ||
      errorMessage.includes("isn't available for quantity")

    if (isProductItemNotAvailable) {
      const stockLevelFromArguments = Number(
        normalizedError?.arguments?.stockLevel ??
          normalizedError?.arguments?.availableQuantity
      )
      const stockLevelFromMessage = extractStockLevelFromMessage(errorMessage)

      // Refetch fresh inventory — cached c_inventory may be stale
      let freshInventory: any = null
      let freshStockLevel: number | null = null
      try {
        const freshProduct = await basket.getProductDetail(parsedProduct.id)
        freshInventory = freshProduct?.c_inventory ?? null
        freshStockLevel = freshInventory?.stockLevel ?? null
      } catch (_) {
        // If refetch fails, fall back to what we can extract from the error
      }

      const stockLevel =
        stockLevelFromMessage ??
        (Number.isFinite(stockLevelFromArguments)
          ? stockLevelFromArguments
          : null) ??
        freshStockLevel

      const cartQuantity =
        (basket.productItems || []).find(
          (item: any) => item.productId === parsedProduct.id
        )?.quantity || 0

      return {
        title:
          stockLevel === null || stockLevel > 0
            ? titleNoOrderableProduct
            : titleSoldOut,
        errorCode: 'product_item_not_available',
        alertMessage: getProductItemNotAvailableDescription(
          stockLevel,
          cartQuantity
        ),
        isError: true,
        availableStockLevel: stockLevel,
        freshInventory
      }
    }

    return {
      errorCode: statusCode.toLowerCase() || errorType,
      alertMessage: errorMessage,
      isError: true
    }
  }

  const onAddToCartHandler = (
    quantity: number,
    basket: any,
    parsedProduct: CommerceBoxProductProps,
    onAddToCart?: (quantity: number) => void
  ) => {
    const productIsGiftCard = parsedProduct.c_isGiftcard || false
    const containsNormalProducts =
      (basket.productItems || []).length > 0 && !basket.c_containsVGC

    if (productIsGiftCard) {
      if (
        (basket.c_containsOnlyNormalGC || containsNormalProducts) &&
        isVirtualGiftCard
      ) {
        setHandleAddToCartError({
          title: titleCantAddProduct,
          errorCode: 'cannot_add_virtual_giftcard',
          alertMessage: formatMessage({
            id: 'msp.commerce_box.add_to_cart.cannot_add_virtual_giftcard',
            defaultMessage:
              'Virtual giftcards cannot be added to the basket if a physical product has already been selected.'
          }),
          isError: true
        })
        alertBoxRef?.current?.showAlert()
        return
      } else if (!isVirtualGiftCard && basket.c_containsVGC) {
        setHandleAddToCartError({
          title: titleCantAddProduct,
          errorCode: 'cannot_add_physical_product',
          alertMessage: descriptionCantAddProduct,
          isError: true
        })
        alertBoxRef?.current?.showAlert()
        return
      }
    } else {
      if (basket.c_containsVGC) {
        setHandleAddToCartError({
          title: titleCantAddProduct,
          errorCode: 'cannot_add_physical_product',
          alertMessage: descriptionCantAddProduct,
          isError: true
        })
        alertBoxRef?.current?.showAlert()
        return
      }
    }

    // WARNING: This function is call wether the product is already in the basket or not.
    // This is possible because a configuration that is done in SaleForce 'Baskets'.
    // However a review with René will be neccesary.
    if (onAddToCart) {
      onAddToCart(quantity)
    }

    const items = []
    const eventTrackingItems: any[] = []
    const productNames: string[] = []
    let hasOutOfStockItems = false
    if (parsedProduct.c_setItemQuantities?.length > 0) {
      const products = parsedProduct.c_setItemQuantities.filter(
        (product: any) => product.productId
      )
      products.map(
        ({
          productId,
          quantity,
          orderable,
          price,
          productName,
          primaryCategoryId
        }: any) => {
          if (orderable) {
            items.push({
              productId,
              quantity,
              price: parsedProduct.price,
              c_productSet: parsedProduct.id
            })
            eventTrackingItems.push({
              item_id: productId,
              quantity,
              item_name: productName,
              item_category: primaryCategoryId,
              item_price: price,
              price:
                quantity > 0
                  ? Math.round((price / quantity) * 100) / 100
                  : price
            })
            productNames.push(productName)
          } else {
            setHandleAddToCartError({
              alertMessage: descriptionNoOrderableProduct,
              errorCode: 'non_orderable_product',
              isWarning: true,
              title: titleNoOrderableProduct
            })
            hasOutOfStockItems = true
          }
        }
      )
    } else {
      let item: any = {
        productId: parsedProduct.id,
        price: parsedProduct.price * quantity,
        quantity
      }

      if (productIsGiftCard) {
        item = {
          ...item,
          c_giftCardIsVirtual: isVirtualGiftCard
        }
      }

      items.push(item)
      const price = product?.c_priceDiscounted || product?.price
      parsedProduct.totalPrice =
        quantity > 0 ? Math.round(price * quantity * 100) / 100 : price
      eventTrackingItems.push(getEventItemFromProduct(parsedProduct, quantity))
      productNames.push(parsedProduct.name)
    }

    basket
      .addItemToBasket(items)
      .then(async (response: any) => {
        if (response?.isError) {
          const errorState = await getAddToCartErrorState(response)
          setHandleAddToCartError(errorState)
          if (errorState.freshInventory) {
            setInventoryOverride(errorState.freshInventory)
          }
          alertBoxRef?.current?.showAlert()
        } else {
          eventTracking('add_to_cart', {
            ecommerce: {
              currency: parsedProduct?.currency,
              items: eventTrackingItems
            }
          })
          EventsEmitter.dispatch(Events.ADD_TO_CART_SUCCESS, [
            { newProducts: productNames }
          ])
          dispatchChoiceOfBonusModal(response)

          if (hasOutOfStockItems) {
            if (alertBoxRef?.current?.showAlert() === undefined) {
              handleAddToCart({
                message: descriptionNoOrderableProduct,
                title: titleNoOrderableProduct,
                type: 'warning',
                position: 'center',
                showBackdrop: true
              })
            } else {
              alertBoxRef?.current?.showAlert()
            }
          }
        }
      })
      .catch(async (error: any) => {
        const errorState = await getAddToCartErrorState(error)
        setHandleAddToCartError(errorState)
        if (errorState.freshInventory) {
          setInventoryOverride(errorState.freshInventory)
        }
        alertBoxRef?.current?.showAlert()
      })
  }

  const onCodeAdded = (hasChoiceOfBonusProducts: boolean) => {
    if (!hasChoiceOfBonusProducts) {
      EventsEmitter.dispatch(Events.ADD_TO_CART_SUCCESS, null)
    }
  }

  useEffect(() => {
    if (!product) setProductSelected(productId)
  }, [productId])

  return !showLoading && (parsedProduct as CommerceBoxProductProps) ? (
    <>
      <CommerceBox
        key={
          inventoryOverride ? `inv-${inventoryOverride.stockLevel}` : 'default'
        }
        isPDP={isPDP}
        showTaxesInfo={showTaxesInfo}
        smallDiscountPrice={smallDiscountPrice}
        innerRef={scrollIntoViewElementRef}
        product={{
          ...parsedProduct,
          variants: noVariants ? [] : parsedProduct.variants,
          variant: noVariants ? null : parsedProduct.variant,
          c_inventory: inventoryOverride || parsedProduct.c_inventory,
          ...(inventoryOverride && {
            inventory: inventoryOverride,
            c_basketLimit:
              Math.min(
                inventoryOverride.stockLevel || 0,
                parsedProduct.c_basketLimit || Infinity
              ) || undefined
          })
        }}
        type={type}
        CTAButton={
          <Button
            innerref={addToCartButtonRef}
            type={CTAButton.type as ButtonTypeProps['type']}
            variant={isDarkMode ? 'outline' : 'default'}
            iconName={CTAButton.icon}
            isDisabled={
              !(inventoryOverride || parsedProduct.c_inventory)?.orderable
            }
            className={`${isDarkMode ? 'border border-white' : ''} ${
              CTAButton.className
            }`}
            ariaLabel={formatMessage(CTAButton.ariaLabel as ButtonLabelProps)}
            data-cy={'add-to-basket-button'}
          >
            {CTAButton.label &&
              (
                formatMessage(CTAButton.label as ButtonLabelProps) || ''
              ).toUpperCase()}
          </Button>
        }
        WishlistButton={
          showWishListButton && (
            <WishlistButton
              product={parsedProduct}
              wishlistedContainerStyle={wishlistButton}
              isWishlistPage={isWishlistPage}
              onWishlistChange={onWishlistChange}
            />
          )
        }
        isDarkMode={isDarkMode}
        isMobile={isMobile}
        onAddToCart={(quantity: number) =>
          onAddToCartHandler(quantity, basket, parsedProduct, onAddToCart)
        }
        showLowestPrice={showLowestPrice}
        onQuantityChange={onQuantityChange}
        onDelete={onDelete}
        onVariantChange={(
          productId: string,
          giftCertIsVirtual: boolean = true
        ) => {
          if (onVariantChange) {
            onVariantChange(productId, giftCertIsVirtual)
            setIsVirtualGiftCard(giftCertIsVirtual)
            if (updateOnVariantChange) {
              setProductSelected(productId)
            }
          } else {
            setProductSelected(productId)
            setIsVirtualGiftCard(giftCertIsVirtual)
          }
        }}
        noPadding={noPadding}
        PromoCode={
          <PromoCode
            applyPromoCode={basket.applyPromoCode}
            bonusDiscountLineItems={basket?.bonusDiscountLineItems || []}
            onCodeAdded={onCodeAdded}
          />
        }
      />
      <AlertBox
        ref={alertBoxRef}
        title={handleAddToCartError?.title}
        description={handleAddToCartError.alertMessage}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
        type={handleAddToCartError?.isWarning ? 'warning' : 'error'}
        autoCloseTime={inventoryOverride?.orderable ? 0 : 4000}
        onClose={() => setHandleAddToCartError({})}
      />
      {showAnchorLink && (
        <CommerceBoxAnchorLink
          productType={(parsedProduct?.c_classCategoryID || '').toLowerCase()}
          CTAButtonRef={addToCartButtonRef}
          scrollTargetRef={scrollIntoViewElementRef}
          icon={<Icons iconName={'Basket'} size='2xl' />}
        />
      )}
    </>
  ) : (
    <></> // TODO: Spinners when the products details is being loaded
  )
}

export default JasminCommerceBox
